import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
// import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useDraftStore from '../../stores/useDraftStore';
import { useShallow } from 'zustand/shallow';

const LoadDraftDialog = () => {
  const navigate = useNavigate();
  const [
    showLoadDialog,
    setShowLoadDialog,
    drafts,
    loadDraftsList,
    loadDraft,
    deleteDraft,
    isLoading,
    error,
    workflowStepToNavigate
  ] = useDraftStore(
    useShallow((state) => [
      state.showLoadDialog,
      state.setShowLoadDialog,
      state.drafts,
      state.loadDraftsList,
      state.loadDraft,
      state.deleteDraft,
      state.isLoading,
      state.error,
      state.workflowStepToNavigate
    ])
  );

  const [selectedDraftId, setSelectedDraftId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);

  const handleClose = () => {
    setShowLoadDialog(false);
    setSelectedDraftId(null);
    setDeletingId(null);
  };

  const handleLoad = async () => {
    if (!selectedDraftId) return;

    const result = await loadDraft(selectedDraftId);
    if (result.success) {
      handleClose();
    }
  };

  // Handle navigation after successful load
  useEffect(() => {
    if (workflowStepToNavigate) {
      const stepRoutes = {
        'ELONGATION': '/design/main/elongation',
        'RECTIFICATION': '/design/main/rectification',
        'TRIMLINE': '/design/main/trimline',
        'THICKNESS': '/design/main/thickness',
        'FLARES': '/design/main/flares',
        'FINALIZATION': '/design/main/finalization'
      };

      const targetRoute = stepRoutes[workflowStepToNavigate];
      if (targetRoute) {
        navigate(targetRoute);

        // Clear the navigation state
        useDraftStore.setState({ workflowStepToNavigate: null });
      }
    }
  }, [workflowStepToNavigate, navigate]);

  const handleDelete = async (draftId, event) => {
    event.stopPropagation();
    setDeletingId(draftId);

    const result = await deleteDraft(draftId);
    if (result.success && selectedDraftId === draftId) {
      setSelectedDraftId(null);
    }

    setDeletingId(null);
  };

  const handleRefresh = () => {
    loadDraftsList();
  };

  const formatDate = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Unknown date';
    }
  };

  const getWorkflowStepLabel = (step) => {
    const stepLabels = {
      'ELONGATION': 'Elongation',
      'RECTIFICATION': 'Rectification',
      'TRIMLINE': 'Trimline',
      'THICKNESS': 'Thickness',
      'FINALIZATION': 'Finalization'
    };
    return stepLabels[step] || step;
  };

  useEffect(() => {
    if (showLoadDialog) {
      loadDraftsList();
    }
  }, [showLoadDialog, loadDraftsList]);

  return (
    <Dialog
      open={showLoadDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Load Draft
        <IconButton
          onClick={handleRefresh}
          disabled={isLoading}
          title="Refresh list"
        >
          ↻
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ minHeight: 300 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              Error loading drafts: {error}
            </Typography>
          ) : drafts.length === 0 ? (
            <Typography align="center" color="text.secondary">
              No drafts found. Save your current state to create a draft.
            </Typography>
          ) : (
            <List>
              {drafts.map((draft, index) => (
                <React.Fragment key={draft.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedDraftId === draft.id}
                      onClick={() => setSelectedDraftId(draft.id)}
                      sx={{ pr: 7 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">
                              {draft.name}
                            </Typography>
                            <Chip
                              label={getWorkflowStepLabel(draft.workflowStep)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            Saved: {formatDate(draft.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleDelete(draft.id, e)}
                      disabled={deletingId === draft.id}
                      sx={{ position: 'absolute', right: 8 }}
                      title="Delete draft"
                    >
                      {deletingId === draft.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        '🗑️'
                      )}
                    </IconButton>
                  </ListItem>
                  {index < drafts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              Error: {error}
            </Typography>
          )}

          {selectedDraftId && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Loading this draft will restore:
                • Complete design state and tool settings
                • 3D model geometry and materials
                • Full undo history for seamless editing
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                ⚠️ Current unsaved changes will be lost
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleLoad}
          variant="contained"
          disabled={!selectedDraftId || isLoading}
        >
          Load Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadDraftDialog;